<template>
    <div class="table-views-menu">
        <v-menu
            v-model="menuOpen"
            offset-y
            left
            :close-on-content-click="false"
            transition="scale-transition"
            max-width="360"
            min-width="300"
            @input="onMenuInput"
        >
            <template v-slot:activator="{ on, attrs }">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                        <button
                            type="button"
                            class="table-toolbar__action"
                            :class="{ 'table-toolbar__action--active': menuOpen }"
                            v-bind="{ ...attrs, ...tooltipAttrs }"
                            v-on="{ ...on, ...tooltipOn }"
                            :aria-label="$translate('savedViews')"
                        >
                            <v-icon small>mdi-bookmark-outline</v-icon>
                            <span
                                v-if="hasActiveView || activeViewId"
                                class="table-toolbar__badge-dot"
                                aria-hidden="true"
                            />
                        </button>
                    </template>
                    <span>{{ $translate("savedViews") }}</span>
                </v-tooltip>
            </template>

            <v-card class="table-views-menu__panel">
                <v-list dense class="py-0">
                    <v-list-item
                        v-if="loading"
                        disabled
                    >
                        <v-list-item-title class="text-caption grey--text">
                            {{ $translate("loading") }}
                        </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                        v-else-if="!views.length"
                        disabled
                    >
                        <v-list-item-title class="text-caption grey--text">
                            {{ $translate("noViews") }}
                        </v-list-item-title>
                    </v-list-item>
                    <v-list-item
                        v-for="(view, index) in views"
                        :key="view.id"
                        :class="{
                            'table-views-menu__item--active': activeViewId === view.id,
                        }"
                        @click="applyView(view)"
                    >
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ viewLabel(view, index) }}
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action class="flex-row ma-0">
                            <v-btn
                                v-if="activeViewId === view.id"
                                icon
                                x-small
                                :aria-label="$translate('saveFilters')"
                                :loading="savingFilters"
                                :disabled="savingFilters || !canSave"
                                @click.stop="saveActiveViewFilters(view)"
                            >
                                <v-icon x-small>mdi-content-save-outline</v-icon>
                            </v-btn>
                            <v-btn
                                icon
                                x-small
                                :aria-label="$translate('renameView')"
                                @click.stop="openRenameDialog(view)"
                            >
                                <v-icon x-small>mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn
                                icon
                                x-small
                                :aria-label="$translate('delete')"
                                @click.stop="confirmDelete(view)"
                            >
                                <v-icon x-small>mdi-delete-outline</v-icon>
                            </v-btn>
                        </v-list-item-action>
                    </v-list-item>
                </v-list>
                <v-divider />
                <div class="pa-2">
                    <button
                        type="button"
                        class="table-views-menu__add"
                        :disabled="!canSave"
                        @click="openCreateDialog"
                    >
                        <v-icon x-small class="mr-1">mdi-plus</v-icon>
                        {{ $translate("createView") }}
                    </button>
                </div>
            </v-card>
        </v-menu>

        <v-dialog v-model="createDialog" max-width="420" persistent>
            <v-card class="table-views-save-dialog">
                <v-card-title class="table-views-save-dialog__title">
                    <div class="table-views-save-dialog__title-icon">
                        <v-icon color="primary" size="22">mdi-bookmark-plus-outline</v-icon>
                    </div>
                    <div>
                        <div class="table-views-save-dialog__heading">
                            {{ $translate("createDialogTitle") }}
                        </div>
                        <div class="table-views-save-dialog__subtitle">
                            {{ $translate("createDialogSubtitle") }}
                        </div>
                    </div>
                </v-card-title>
                <v-card-text class="table-views-save-dialog__body">
                    <v-text-field
                        v-model="createName"
                        :label="$translate('viewNameLabel')"
                        :error-messages="createNameErrors"
                        outlined
                        dense
                        autofocus
                        counter="128"
                        maxlength="128"
                        hide-details="auto"
                        @keyup.enter="confirmCreate"
                    />
                </v-card-text>
                <v-card-actions class="table-views-save-dialog__actions">
                    <v-spacer />
                    <v-btn text class="px-3" @click="closeCreateDialog">
                        {{ $translate("cancel") }}
                    </v-btn>
                    <v-btn
                        color="primary"
                        class="px-4"
                        depressed
                        :loading="saving"
                        :disabled="saving"
                        @click="confirmCreate"
                    >
                        {{ $translate("create") }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="renameDialog" max-width="420" persistent>
            <v-card class="table-views-save-dialog">
                <v-card-title class="table-views-save-dialog__title">
                    <div class="table-views-save-dialog__title-icon">
                        <v-icon color="primary" size="22">mdi-pencil-outline</v-icon>
                    </div>
                    <div>
                        <div class="table-views-save-dialog__heading">
                            {{ $translate("renameDialogTitle") }}
                        </div>
                        <div class="table-views-save-dialog__subtitle">
                            {{ $translate("renameDialogSubtitle") }}
                        </div>
                    </div>
                </v-card-title>
                <v-card-text class="table-views-save-dialog__body">
                    <v-text-field
                        v-model="renameName"
                        :label="$translate('viewNameLabel')"
                        :error-messages="renameNameErrors"
                        outlined
                        dense
                        autofocus
                        counter="128"
                        maxlength="128"
                        hide-details="auto"
                        @keyup.enter="confirmRename"
                    />
                </v-card-text>
                <v-card-actions class="table-views-save-dialog__actions">
                    <v-spacer />
                    <v-btn text class="px-3" @click="closeRenameDialog">
                        {{ $translate("cancel") }}
                    </v-btn>
                    <v-btn
                        color="primary"
                        class="px-4"
                        depressed
                        :loading="renaming"
                        :disabled="renaming"
                        @click="confirmRename"
                    >
                        {{ $translate("rename") }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "@/plugins/axios";
import GlobalMixins from "@/mixins/global";
import {
    extractApiFieldErrors,
    formatViewLabel,
    loadActiveViewId,
    saveActiveViewId,
    sanitizeFiltersForView,
} from "@/utils/tableViews";
import { migrateCatalogEntryFormatFilter } from "@/utils/apiFilterParams";

export default {
    name: "TableViewsMenu",
    mixins: [GlobalMixins],
    props: {
        viewsScope: {
            type: String,
            required: true,
        },
        filters: {
            type: Object,
            required: true,
        },
        viewsExcludeKeys: {
            type: Array,
            default: () => [],
        },
        hasActiveView: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            menuOpen: false,
            views: [],
            loading: false,
            saving: false,
            savingFilters: false,
            renaming: false,
            activeViewId: null,
            createDialog: false,
            createName: "",
            createNameErrors: [],
            renameDialog: false,
            renameViewId: null,
            renameName: "",
            renameNameErrors: [],
        };
    },
    computed: {
        sanitizedCurrentFilters() {
            return sanitizeFiltersForView(this.filters, this.viewsExcludeKeys);
        },
        canSave() {
            return Object.keys(this.sanitizedCurrentFilters).length > 0;
        },
        renameTargetView() {
            if (!this.renameViewId) {
                return null;
            }
            return this.views.find((view) => view.id === this.renameViewId) || null;
        },
        activeViewName() {
            const view = this.views.find((v) => v.id === this.activeViewId);
            if (!view) {
                return null;
            }
            const index = this.views.indexOf(view);
            return this.viewLabel(view, index);
        },
    },
    watch: {
        viewsScope: {
            immediate: true,
            handler() {
                this.fetchViews();
            },
        },
        activeViewId(id) {
            saveActiveViewId(this.viewsScope, id);
            this.$emit("active-view-change", {
                id,
                name: this.activeViewName,
            });
        },
    },
    methods: {
        onMenuInput(open) {
            this.menuOpen = open;
        },
        restoreActiveViewFromStorage() {
            const savedId = loadActiveViewId(this.viewsScope);
            if (savedId === null || savedId === undefined) {
                return;
            }
            const view = this.views.find(
                (item) => String(item.id) === String(savedId),
            );
            if (!view || JSON.stringify(sanitizeFiltersForView(view.filters, this.viewsExcludeKeys)) !== JSON.stringify(this.sanitizedCurrentFilters)) {
                saveActiveViewId(this.viewsScope, null);
                return;
            }
            this.activeViewId = view.id;
        },
        clearActiveView() {
            this.activeViewId = null;
        },
        viewLabel(view, index) {
            return formatViewLabel(view, index);
        },
        async fetchViews() {
            if (!this.viewsScope) {
                this.views = [];
                return;
            }
            this.loading = true;
            try {
                const response = await axios.get("/api/tableviews/", {
                    params: { scope: this.viewsScope },
                });
                this.views = response.data.results || response.data || [];
                this.restoreActiveViewFromStorage();
            } catch (error) {
                this.views = [];
                console.warn("Failed to load table views", error);
            } finally {
                this.loading = false;
            }
        },
        openCreateDialog() {
            if (!this.canSave) {
                return;
            }
            this.createName = "";
            this.createNameErrors = [];
            this.createDialog = true;
            this.menuOpen = false;
        },
        closeCreateDialog() {
            this.createDialog = false;
            this.createName = "";
            this.createNameErrors = [];
        },
        async confirmCreate() {
            const filters = this.sanitizedCurrentFilters;
            if (!Object.keys(filters).length) {
                return;
            }
            const name = (this.createName || "").trim();
            this.createNameErrors = [];
            if (!name) {
                this.createNameErrors = [this.$translate("viewNameRequired")];
                return;
            }
            await this.createView(name, filters);
        },
        openRenameDialog(view) {
            this.renameViewId = view.id;
            this.renameName = view.name || "";
            this.renameNameErrors = [];
            this.renameDialog = true;
            this.menuOpen = false;
        },
        closeRenameDialog() {
            this.renameDialog = false;
            this.renameViewId = null;
            this.renameName = "";
            this.renameNameErrors = [];
        },
        async confirmRename() {
            const view = this.renameTargetView;
            if (!view) {
                return;
            }
            const name = (this.renameName || "").trim();
            this.renameNameErrors = [];
            if (!name) {
                this.renameNameErrors = [this.$translate("viewNameRequired")];
                return;
            }
            if (name === view.name) {
                this.closeRenameDialog();
                return;
            }
            this.renaming = true;
            try {
                const response = await axios.patch(`/api/tableviews/${view.id}/`, {
                    name,
                });
                const index = this.views.findIndex((item) => item.id === view.id);
                if (index >= 0) {
                    this.$set(this.views, index, response.data);
                }
                if (this.activeViewId === response.data.id) {
                    this.$emit("active-view-change", {
                        id: response.data.id,
                        name: this.activeViewName,
                    });
                }
                this.addAlert(this.$translate("viewRenamed"));
                this.closeRenameDialog();
            } catch (xhr_error) {
                this.handleRenameError(xhr_error);
            } finally {
                this.renaming = false;
            }
        },
        async saveActiveViewFilters(view) {
            const filters = this.sanitizedCurrentFilters;
            if (!Object.keys(filters).length) {
                return;
            }
            this.savingFilters = true;
            try {
                const response = await axios.patch(`/api/tableviews/${view.id}/`, {
                    filters,
                });
                const index = this.views.findIndex((item) => item.id === view.id);
                if (index >= 0) {
                    this.$set(this.views, index, response.data);
                }
                this.activeViewId = response.data.id;
                this.addAlert(this.$translate("viewUpdated"));
            } catch (xhr_error) {
                this.handleSaveError(xhr_error);
            } finally {
                this.savingFilters = false;
            }
        },
        async createView(name, filters) {
            this.saving = true;
            try {
                const response = await axios.post("/api/tableviews/", {
                    scope: this.viewsScope,
                    name,
                    filters,
                });
                this.views = [response.data, ...this.views];
                this.activeViewId = response.data.id;
                this.addAlert(this.$translate("viewSaved"));
                this.closeCreateDialog();
            } catch (xhr_error) {
                this.handleSaveError(xhr_error);
            } finally {
                this.saving = false;
            }
        },
        handleSaveError(xhr_error) {
            const error = this.$storeutils.handleError(xhr_error);
            const nameErrors = extractApiFieldErrors(error.details, "name");
            if (nameErrors?.length) {
                this.createNameErrors = nameErrors;
                return;
            }
            const filterErrors = extractApiFieldErrors(error.details, "filters");
            if (filterErrors?.length) {
                this.$swal({
                    title: this.$translate("saveFailed"),
                    html: filterErrors.join("<br/>"),
                    type: "error",
                });
                return;
            }
            this.$swal({
                title: this.$translate("saveFailed"),
                html: this.$utils.resultToSwalContent(this, error),
                type: "error",
            });
        },
        handleRenameError(xhr_error) {
            const error = this.$storeutils.handleError(xhr_error);
            const nameErrors = extractApiFieldErrors(error.details, "name");
            if (nameErrors?.length) {
                this.renameNameErrors = nameErrors;
                return;
            }
            this.$swal({
                title: this.$translate("renameFailed"),
                html: this.$utils.resultToSwalContent(this, error),
                type: "error",
            });
        },
        applyView(view) {
            if (this.activeViewId === view.id) {
                this.clearView();
                return;
            }
            this.activeViewId = view.id;
            this.menuOpen = false;
            this.$emit("apply", migrateCatalogEntryFormatFilter({ ...view.filters }));
        },
        clearView() {
            this.activeViewId = null;
            this.$emit("reset");
        },
        async confirmDelete(view) {
            const result = await this.$swal({
                title: this.$translate("deleteConfirmTitle"),
                text: this.$translate("deleteConfirmText", {
                    name: view.name || this.viewLabel(view, 0),
                }),
                type: "warning",
                showCancelButton: true,
                confirmButtonText: this.$translate("delete"),
                cancelButtonText: this.$translate("cancel"),
            });
            if (result.dismiss) {
                return;
            }
            try {
                await axios.delete(`/api/tableviews/${view.id}/`);
                this.views = this.views.filter((v) => v.id !== view.id);
                if (this.activeViewId === view.id) {
                    this.activeViewId = null;
                }
                this.addAlert(this.$translate("viewDeleted"));
            } catch (xhr_error) {
                this.$swal({
                    title: this.$translate("deleteFailed"),
                    html: this.$utils.resultToSwalContent(
                        this,
                        this.$storeutils.handleError(xhr_error),
                    ),
                    type: "error",
                });
            }
        },
        resetFilters() {
            this.activeViewId = null;
            this.$emit("reset");
        },
    },
    i18n: {
        messages: {
            en: {
                savedViews: "Saved views",
                loading: "Loading…",
                noViews: "No saved views yet",
                createView: "Create a view",
                saveFilters: "Save filters to this view",
                renameView: "Rename view",
                createDialogTitle: "Create a view",
                createDialogSubtitle: "Save the current filters under a new name.",
                renameDialogTitle: "Rename view",
                renameDialogSubtitle: "Choose a new name for this saved view.",
                viewNameLabel: "View name",
                viewNameRequired: "Enter a name for this view",
                create: "Create",
                rename: "Rename",
                cancel: "Cancel",
                viewSaved: "Filter view saved",
                viewUpdated: "Filter view updated",
                viewRenamed: "View renamed",
                saveFailed: "Could not save view",
                renameFailed: "Could not rename view",
                deleteConfirmTitle: "Delete this view?",
                deleteConfirmText:
                    'Delete "{name}"? This will remove the saved filters for everyone in your organization.',
                delete: "Delete",
                viewDeleted: "Filter view deleted",
                deleteFailed: "Could not delete view",
            },
            fr: {
                savedViews: "Vues sauvegardées",
                loading: "Chargement…",
                noViews: "Aucune vue enregistrée",
                createView: "Créer une vue",
                saveFilters: "Enregistrer les filtres sur cette vue",
                renameView: "Renommer la vue",
                createDialogTitle: "Créer une vue",
                createDialogSubtitle: "Enregistrez les filtres actuels sous un nouveau nom.",
                renameDialogTitle: "Renommer la vue",
                renameDialogSubtitle: "Choisissez un nouveau nom pour cette vue.",
                viewNameLabel: "Nom de la vue",
                viewNameRequired: "Indiquez un nom pour cette vue",
                create: "Créer",
                rename: "Renommer",
                cancel: "Annuler",
                viewSaved: "Vue de filtres enregistrée",
                viewUpdated: "Vue de filtres mise à jour",
                viewRenamed: "Vue renommée",
                saveFailed: "Impossible d'enregistrer la vue",
                renameFailed: "Impossible de renommer la vue",
                deleteConfirmTitle: "Supprimer cette vue ?",
                deleteConfirmText:
                    'Supprimer « {name} » ? Les filtres enregistrés seront retirés pour toute l\'organisation.',
                delete: "Supprimer",
                viewDeleted: "Vue de filtres supprimée",
                deleteFailed: "Impossible de supprimer la vue",
            },
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/variables.scss";

.table-views-menu__panel {
    max-height: 400px;
    overflow-y: auto;

    ::v-deep .v-list-item {
        align-items: center;
        min-height: 40px;
    }

    ::v-deep .v-list-item__action {
        align-self: center;
        margin-top: 0 !important;
        margin-bottom: 0 !important;
        display: inline-flex;
        flex-direction: row;
        align-items: center;
        height: auto;
    }
}

.table-views-menu__item--active {
    background: $color-primary-extralight !important;
    border: 1px solid $color-primary-light;
    border-radius: 6px;
    margin: 2px 4px;
}

.table-views-menu__add {
    display: inline-flex;
    align-items: center;
    width: 100%;
    font-size: 13px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.87);
    background: none;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    padding: 8px;
    text-decoration: none;
    transition: background 0.15s ease;

    &:hover,
    &:focus {
        text-decoration: none;
        background: rgba(0, 0, 0, 0.04);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        background: none;
    }
}
</style>

<style lang="scss">
@import "@/assets/css/variables.scss";

.table-views-save-dialog {
    border-radius: 12px !important;
    overflow: hidden;
}

.table-views-save-dialog__title {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 20px 24px 8px !important;
}

.table-views-save-dialog__title-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: $color-primary-extralight;
}

.table-views-save-dialog__heading {
    font-size: 18px;
    font-weight: 600;
    line-height: 1.3;
    color: rgba(0, 0, 0, 0.87);
}

.table-views-save-dialog__subtitle {
    margin-top: 2px;
    font-size: 13px;
    font-weight: 400;
    line-height: 1.4;
    color: rgba(0, 0, 0, 0.77);
}

.table-views-save-dialog__body {
    padding: 8px 24px 8px !important;
}

.table-views-save-dialog__actions {
    padding: 8px 16px 16px !important;
    gap: 4px;
}

.table-views-menu .table-toolbar__action {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    color: $color-gs-extrabold;
    transition: background 0.15s ease;

    &:hover {
        background: $color-gs-extralight;
    }

    &--active {
        background: $color-gs-light;
    }
}

.table-views-menu .table-toolbar__badge-dot {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: $color-info;
    border: 1.5px solid $color-gs-white;
}
</style>
